import z from 'zod'

/** ICE candidate as sent over JSON (RTCIceCandidateInit-compatible). */
export const audienceWebRtcIceCandidateSchema = z.object({
  candidate: z.string(),
  sdpMid: z.string().nullable().optional(),
  sdpMLineIndex: z.number().int().nullable().optional()
}).strict()

export type TAudienceWebRtcIceCandidate = z.infer<typeof audienceWebRtcIceCandidateSchema>

/**
 * WebRTC signaling envelope (operator ↔ player via Tenant DO + Users Directory WS).
 * `playerId` is included on the wire so the management app can route to the correct peer connection.
 */
export const audienceWebRtcSignalPayloadSchema = z.object({
  sessionId: z.string().uuid(),
  playerId: z.string().min(1),
  kind: z.enum(['offer', 'answer', 'ice', 'bye']),
  sdp: z.string().min(1).optional(),
  candidate: audienceWebRtcIceCandidateSchema.optional()
}).strict()

export type TAudienceWebRtcSignalPayload = z.infer<typeof audienceWebRtcSignalPayloadSchema>

/** Request body for POST …/audience-webrtc/signal (playerId from URL). */
export const audienceWebRtcSignalRequestBodySchema = z.object({
  sessionId: z.string().uuid(),
  kind: z.enum(['offer', 'answer', 'ice', 'bye']),
  sdp: z.string().min(1).optional(),
  candidate: audienceWebRtcIceCandidateSchema.optional()
}).strict().superRefine((v, ctx) => {
  if (v.kind === 'offer' || v.kind === 'answer') {
    if (v.sdp == null || v.sdp.length === 0) {
      ctx.addIssue({ code: 'custom', message: 'sdp is required for offer and answer' })
    }
  }
  if (v.kind === 'ice') {
    if (v.candidate == null) {
      ctx.addIssue({ code: 'custom', message: 'candidate is required for ice' })
    }
  }
  if (v.kind === 'bye') {
    if (v.sdp != null) {
      ctx.addIssue({ code: 'custom', message: 'sdp must not be set for bye' })
    }
    if (v.candidate != null) {
      ctx.addIssue({ code: 'custom', message: 'candidate must not be set for bye' })
    }
  }
})

export type TAudienceWebRtcSignalRequestBody = z.infer<typeof audienceWebRtcSignalRequestBodySchema>
